/**
 * 路由守卫工具函数
 * 提供认证检查、权限验证等功能
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { 
  ROUTE_GUARD_CONFIG, 
  requiresAuthentication, 
  requiresQuestionnaire,
  REDIRECT_IF_AUTH_ROUTES 
} from './config'

/**
 * 创建带超时的认证检查器
 * @param timeoutMs 超时时间（毫秒）
 * @returns 认证检查函数
 */
export const createAuthCheckWithTimeout = (timeoutMs: number = 2000) => {
  return async (authStore: any): Promise<boolean> => {
    // 如果已经认证，直接返回
    if (authStore.isAuthenticated) {
      return true
    }
    
    // 如果正在初始化，等待完成（带超时）
    if (authStore.isInitializing) {
      console.log('[Router Guards] 认证正在初始化中，等待完成...')

      const timeoutPromise = new Promise<boolean>((resolve) => {
        setTimeout(() => {
          console.warn('[Router Guards] 认证检查超时，使用当前状态')
          resolve(authStore.isAuthenticated)
        }, timeoutMs)
      })

      const authPromise = new Promise<boolean>((resolve) => {
        const checkAuth = () => {
          if (!authStore.isInitializing) {
            resolve(authStore.isAuthenticated)
          } else {
            setTimeout(checkAuth, 50)
          }
        }
        checkAuth()
      })

      return Promise.race([authPromise, timeoutPromise])
    }

    return authStore.isAuthenticated
  }
}

/**
 * 检查用户是否有访问特定页面的权限
 * @param to 目标路由
 * @param authStore 认证store
 * @returns 是否有权限
 */
export const checkPagePermission = async (
  to: RouteLocationNormalized,
  authStore: any
): Promise<{ hasPermission: boolean; redirectTo?: string; reason?: string }> => {
  const routeName = to.name as string
  
  // 检查是否需要认证
  if (to.meta.requiresAuth || requiresAuthentication(routeName)) {
    const checkAuthWithTimeout = createAuthCheckWithTimeout()
    const isAuthenticated = await checkAuthWithTimeout(authStore)

    if (!isAuthenticated) {
      return {
        hasPermission: false,
        redirectTo: '/login',
        reason: '需要登录才能访问此页面'
      }
    }

    // 对于需要认证的页面，检查问卷完成状态
    if (requiresQuestionnaire(routeName)) {
      try {
        const hasCompletedQuestionnaire = await authStore.checkQuestionnaireStatus()
        if (!hasCompletedQuestionnaire) {
          return {
            hasPermission: false,
            redirectTo: '/questionnaire',
            reason: '需要完成问卷才能访问此页面'
          }
        }
      } catch (error) {
        console.error('[Router Guards] 检查问卷状态失败:', error)

        // 如果是认证错误，清除认证信息
        if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
          authStore.clearAuth()
          return {
            hasPermission: false,
            redirectTo: '/login',
            reason: '认证已过期，请重新登录'
          }
        }
      }
    }
  }

  return { hasPermission: true }
}

/**
 * 处理已登录用户访问登录/注册页的重定向
 * @param to 目标路由
 * @param authStore 认证store
 * @returns 重定向信息
 */
export const handleAuthRedirect = async (
  to: RouteLocationNormalized,
  authStore: any
): Promise<{ shouldRedirect: boolean; redirectTo?: string }> => {
  const routeName = to.name as string
  
  if ((to.meta.redirectIfAuth || REDIRECT_IF_AUTH_ROUTES.includes(routeName)) && authStore.isAuthenticated) {
    try {
      const hasCompletedQuestionnaire = await authStore.checkQuestionnaireStatus()

      return {
        shouldRedirect: true,
        redirectTo: hasCompletedQuestionnaire ? '/dashboard' : '/questionnaire'
      }
    } catch (error) {
      console.error('[Router Guards] 检查问卷状态失败:', error)
      return {
        shouldRedirect: true,
        redirectTo: '/dashboard'
      }
    }
  }

  return { shouldRedirect: false }
}

/**
 * 设置页面标题
 * @param to 目标路由
 */
export const setPageTitle = (to: RouteLocationNormalized): void => {
  if (to.meta.title) {
    document.title = `${to.meta.title} - Carbon Trading Platform`
  }
}

/**
 * 刷新余额信息（如果需要）
 * @param to 目标路由
 * @param authStore 认证store
 */
export const refreshBalanceIfNeeded = async (
  to: RouteLocationNormalized,
  authStore: any
): Promise<void> => {
  const routeName = to.name as string
  
  // 如果用户已认证且访问需要余额信息的页面，刷新余额
  if (ROUTE_GUARD_CONFIG.enableBalanceRefresh &&
      authStore.isAuthenticated && 
      routeName && 
      ROUTE_GUARD_CONFIG.balanceRequiredPages?.includes(routeName)) {
    console.log(`[Router Guards] 刷新余额信息，页面: ${String(to.name)}`)
    try {
      const { useBalanceStore } = await import('@/stores/balance')
      const balanceStore = useBalanceStore()
      balanceStore.refreshBalance().catch(error => {
        console.error('[Router Guards] 余额刷新失败:', error)
      })
    } catch (error) {
      console.error('[Router Guards] 导入余额store失败:', error)
    }
  }
}

/**
 * 主路由守卫函数
 * @param to 目标路由
 * @param from 来源路由
 * @param next 导航函数
 */
export const mainNavigationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  console.log(`[Router Guards] 导航从 ${from.path} 到 ${to.path}`)

  // 设置页面标题
  setPageTitle(to)

  // 简化路由守卫 - 暂时允许所有导航通过
  console.log(`[Router Guards] 允许导航到: ${to.path}`)
  next()
}